import React, { useState, useEffect } from "react";
import { X, CircleAlert } from "lucide-react";

interface DeletePageModalProps {
  currentPageIndex: number;
  onConfirmDelete: () => void;
  isDeleting?: boolean;
  isOpen: boolean;
  onClose: () => void;
  isLastPage?: boolean;
}

const DeletePageModal: React.FC<DeletePageModalProps> = ({
  currentPageIndex,
  onConfirmDelete,
  isDeleting = false,
  isOpen,
  onClose,
  isLastPage = false,
}) => {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setVisible(true);
        });
      });
    } else {
      setVisible(false);
      const timer = setTimeout(() => setMounted(false), 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!mounted) return null;

  return (
    <div
      id="deletePageModal"
      tabIndex={-1}
      className={`fixed inset-0 z-50 flex justify-center items-start overflow-y-auto transition-opacity duration-200 ${
        visible ? "bg-black/50 opacity-100" : "opacity-0 pointer-events-none"
      }`}
      aria-hidden={!isOpen}
    >
      <div className="relative p-4 w-full max-w-md mt-20">
        <div className={`relative bg-white rounded-lg border border-gray-200 p-5 transition-all duration-200 ${
          visible ? "opacity-100 scale-100" : "opacity-0 scale-95"
        }`}>
          <button
            type="button"
            className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
            <span className="sr-only">Close modal</span>
          </button>

          <div className="text-center pt-2">
            <CircleAlert className="mx-auto mb-4 text-gray-300 w-10 h-10" aria-hidden="true" />

            <p className="text-sm text-gray-600 mb-6">
              {isLastPage ? (
                <>
                  This is the only page in the document. Deleting it will{" "}
                  <strong className="text-gray-900">delete the entire document</strong>.
                </>
              ) : (
                <>
                  Are you sure you want to delete{" "}
                  <strong className="text-gray-900">page {currentPageIndex + 1}</strong>?
                </>
              )}
            </p>

            <div className="flex items-center gap-2 justify-center">
              <button
                type="button"
                className="h-9 px-4 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="h-9 px-4 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={onConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeletePageModal;
